import { Customer, PaymentType } from '@bulthaup/api/db';
import { getProjectAmounts } from '@bulthaup/api/entities/budget';
import { sum } from '@bulthaup/api/utils';
import { Stack, Text } from '@mantine/core';
import { Decimal } from 'decimal.js';

import { trpc } from '../../../../../../services/trpc';
import { formatAmount } from '../../../../../../utils/format';
import { FinalBalance } from './-final-balance';
import { PaymentList } from './-payment-list';

export function PaymentSection({ customer }: { customer: Customer }) {
  const { data: payments = [] } = trpc.projects.listPayments.useQuery({
    customerId: customer.id,
  });

  const { data: credits = [] } = trpc.projects.listCredits.useQuery({
    customerId: customer.id,
  });

  const { data: projects } = trpc.projects.listCustomerProjects.useQuery({
    customerId: customer.id,
  });

  const grossTotalAmount = projects?.reduce(
    (acc, project) =>
      acc.add(sum(Object.values(getProjectAmounts(project).gross))),
    new Decimal(0),
  );

  const grossTotalAmountMinusCredit = grossTotalAmount?.sub(
    sum(credits.map(credit => credit.amount ?? new Decimal(0))),
  );

  const getPaymentsByType = (type: PaymentType) => {
    return payments.filter(payment => payment.type === type);
  };

  const totalPayments = sum(
    payments.map(payment => payment.amount ?? new Decimal(0)),
  );

  const requiredAmountForOrder = new Decimal(0.35)
    .mul(grossTotalAmountMinusCredit ?? new Decimal(0))
    .sub(totalPayments);
  const requiredAmountForDelivery = new Decimal(0.95)
    .mul(grossTotalAmountMinusCredit ?? new Decimal(0))
    .sub(totalPayments);

  return (
    <Stack>
      <Stack gap="md">
        <PaymentList
          type={PaymentType.ON_ORDER}
          payments={getPaymentsByType(PaymentType.ON_ORDER)}
        />
        {requiredAmountForOrder.greaterThan(0) && (
          <Text ta="center" c="red">
            Montant requis pour la commande:{' '}
            <b>{formatAmount(requiredAmountForOrder)}</b>
          </Text>
        )}
        <PaymentList
          type={PaymentType.ON_DELIVERY}
          payments={getPaymentsByType(PaymentType.ON_DELIVERY)}
        />
        {requiredAmountForOrder.lessThan(0) &&
          requiredAmountForDelivery.greaterThan(0) && (
            <Text ta="center" c="red">
              Montant requis pour passer la livraison :{' '}
              <b>{formatAmount(requiredAmountForDelivery)}</b>
            </Text>
          )}
        <PaymentList
          type={PaymentType.FINAL}
          payments={getPaymentsByType(PaymentType.FINAL)}
        />
      </Stack>
      <FinalBalance customerId={customer.id} />
    </Stack>
  );
}
